import React, { useCallback } from "react";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import { FiFileText, FiRefreshCw } from "react-icons/fi";
import { tokens, surface, hairline } from "../../../styles/designTokens";
import { eyebrow, mono } from "../styles";

const Dropzone = styled.div`
  flex: 1;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 40px 24px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 2px dashed ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusLg};
  cursor: pointer;
  text-align: center;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover { border-color: ${tokens.brand}; background: ${tokens.brandSoft}; }

  &.active {
    border-color: ${tokens.brand};
    background: ${tokens.brandSoft};
    box-shadow: 0 0 0 4px ${tokens.brandRing};
  }

  &.has-file {
    border-style: solid;
    border-color: ${tokens.brand};
    background: ${tokens.brandSoft};

    .drop-icon {
      background: ${tokens.brand};
      border-color: ${tokens.brand};
      color: white;
    }
  }

  .drop-icon {
    width: 52px;
    height: 52px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: 50%;
    color: ${tokens.brand};
    transition: all ${tokens.durBase} ${tokens.easeOut};

    svg { font-size: 1.4em; }
  }

  .drop-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .drop-eyebrow { ${eyebrow}; font-size: 0.58em; opacity: 0.5; }

    h4 {
      margin: 4px 0 2px 0;
      font-size: 0.92em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.68em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      letter-spacing: 0.01em;
    }
  }

  .replace-hint {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    padding: 6px 12px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    font-size: 0.68em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    opacity: 0.75;

    svg { font-size: 0.9em; }
  }
`;

const formatBytes = (bytes) => {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

/**
 * Dropzone reutilizável para upload do template HTML da campanha.
 * Lê o arquivo com FileReader e devolve { fileName, size, htmlContent }.
 */
export default function TemplateDropzone({ file, onFileAccepted }) {
  const onDrop = useCallback(
    (acceptedFiles) => {
      const htmlFile = acceptedFiles[0];
      if (!htmlFile) return;

      const reader = new FileReader();
      reader.onload = () => {
        onFileAccepted({
          fileName: htmlFile.name,
          size: htmlFile.size,
          htmlContent: reader.result,
        });
      };
      reader.readAsText(htmlFile);
    },
    [onFileAccepted]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/html": [".html", ".htm"] },
    multiple: false,
  });

  return (
    <Dropzone
      {...getRootProps()}
      className={`${isDragActive ? "active" : ""} ${file ? "has-file" : ""}`}
    >
      <input {...getInputProps()} />
      <div className="drop-icon">
        <FiFileText />
      </div>
      {file ? (
        <div className="drop-text">
          <span className="drop-eyebrow">ARQUIVO CARREGADO</span>
          <h4>{file.fileName}</h4>
          <p>{formatBytes(file.size)}</p>
          <span className="replace-hint">
            <FiRefreshCw /> Clique ou arraste para substituir
          </span>
        </div>
      ) : (
        <div className="drop-text">
          <span className="drop-eyebrow">UPLOAD DE TEMPLATE</span>
          <h4>Arraste o arquivo .html aqui</h4>
          <p>ou clique para selecionar do computador</p>
        </div>
      )}
    </Dropzone>
  );
}
